/**
 * Format CPF with mask (e.g., "12345678910" -> "123.456.789-10")
 * @param cpf - CPF string (numbers only or already formatted)
 * @returns Formatted CPF string
 */
export const formatCPF = (cpf: string): string => {
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11) return cpf;
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Format CNPJ with mask (e.g., "12345678000199" -> "12.345.678/0001-99")
 * @param cnpj - CNPJ string (numbers only or already formatted)
 * @returns Formatted CNPJ string
 */
export const formatCNPJ = (cnpj: string): string => {
  const cleaned = cnpj.replace(/\D/g, '');
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

/**
 * Format phone number with mask (e.g., "11999998888" -> "(11) 99999-8888")
 * Supports both landline (10 digits) and mobile (11 digits)
 * @param phone - Phone string (numbers only or already formatted)
 * @returns Formatted phone string
 */
export const formatPhone = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length === 11) {
    // Mobile: (11) 99999-8888
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }

  if (cleaned.length === 10) {
    // Landline: (11) 9999-8888
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }

  return phone;
};

/**
 * Format value based on property type
 * @param value - The value to format
 * @param property - The property type (cpf, cnpj, celular, etc.)
 * @returns Formatted value
 */
export const formatByProperty = (
  value: string,
  property: 'cpf' | 'cnpj' | 'email' | 'celular' | 'telefone'
): string => {
  switch (property) {
    case 'cpf':
      return formatCPF(value);
    case 'cnpj':
      return formatCNPJ(value);
    case 'celular':
    case 'telefone':
      return formatPhone(value);
    case 'email':
    default:
      return value;
  }
};
